import React from 'react';
import { findDOMNode } from 'react-dom';
import { injectIntl } from 'react-intl';
import { connect } from 'react-redux';
import {
  Grid,
  Row,
  Col,
  DropdownButton,
  MenuItem,
  Button,
  Alert,
  Clearfix,
  Form,
  FormGroup,
  InputGroup,
  FormControl,
} from 'react-bootstrap';
import Modal from 'components/Modal';
import { forEach as _forEach, find as _find } from 'lodash';

import { changeUploadContext, resetUploadContext, fileUploadHelper } from 'actions/UploadActions';
import { listDispatchHelper, changeListSort } from 'actions/ListActions';
import { deleterDispatchHelper } from 'actions/DeleterActions';

import Loader from 'components/layout/Loader';

import CmsMediaSelectorFilePreview from 'components/cms/settings/components/CmsMediaSelectorFilePreview';
import CmsMediaSelectorUploadFilePreview from 'components/cms/settings/components/CmsMediaSelectorUploadFilePreview';
import CmsMediaSelectorFileData from 'components/cms/settings/components/CmsMediaSelectorFileData';

class CmsMedia extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      displayMode: localStorage.getItem('CmsMediaDisplayMode') || 'th',
      activePage: 1,
      resPerPage: 50,
      searchText: '',
      urlData: { filters: { type: props.urlType } },
      mediaList: [],
      selected: null,
      headDimensions: {
        width: -1,
        height: -1,
      },
      activeFilter: null,
    };

    this.scrollContainerRef = React.createRef();
  }

  componentDidUpdate = (prevProps, prevState) => {
    const {
      show,
      itemId,
      pageId,
      urlType,
      selectedFile,
      user,
      list,
      upload,
      fileDeleter,
      updateUploadContext,
      doResetUploadContext,
    } = this.props;
    const { mediaList: stateMediaList, activeFilter } = this.state;

    if (!fileDeleter.pending && !fileDeleter.hasError && prevProps.fileDeleter.pending) {
      const mediaList = stateMediaList.slice().filter(file => file.id !== fileDeleter.data);
      this.setState({ mediaList });
    }

    if (!prevProps.show && show) {
      let uploadContext = {
        type: 'cms',
        uploadLink: '/api/cmsMedia/files.json',
      };
      if (itemId) {
        uploadContext = {
          ...uploadContext,
          data: {
            ...uploadContext.data,
            links: [{ type: 'item', id: itemId }],
          },
        };
      } else if (pageId) {
        uploadContext = {
          ...uploadContext,
          data: {
            ...uploadContext.data,
            links: [{ type: 'page', id: pageId }],
          },
        };
      }
      updateUploadContext(uploadContext);

      if (stateMediaList.length === 0) {
        this.fetchInitialData();
      }

      if (selectedFile) {
        this.setState({ selected: selectedFile });
      }
    }

    if (prevProps.show && !show) {
      doResetUploadContext();
    }

    if (prevProps.list.pending && !list.pending && !list.hasError) {
      this.setState(
        prevState => ({
          mediaList: [...prevState.mediaList, ...list.data.list],
        }),
        () => {
          this.loadMoreTimeout = setTimeout(() => {
            // check if we need to load more images when there is no scrollbar
            if (this.scrollContainerRef.current) {
              const { mediaList: nextStateMediaList } = this.state;

              const { scrollHeight, scrollTop, clientHeight } = this.scrollContainerRef.current;
              const bottom = scrollHeight - scrollTop === clientHeight;

              if (bottom) {
                if (nextStateMediaList.length < list.data.count) {
                  this.loadMore();
                }
              }
            }
          }, 300);
        }
      );
    }

    if (prevState.activeFilter !== activeFilter || prevProps.urlType !== urlType) {
      const urlDataFilters = {};
      switch (activeFilter) {
        case 'self':
          urlDataFilters.creator = user.username;
          break;

        case 'page':
          urlDataFilters['links.type'] = 'page';
          urlDataFilters['links.id'] = pageId;
          break;

        case 'item':
          urlDataFilters['links.type'] = 'item';
          urlDataFilters['links.id'] = itemId;
          break;
      }

      if (urlType) {
        urlDataFilters.type = urlType;
      }

      this.setState(
        prevState => ({
          mediaList: [],
          urlData: { ...prevState.urlData, filters: urlDataFilters },
        }),
        () => this.fetchData()
      );
    }

    if (prevProps.upload.isUploading) {
      // check if any file that is uploading, finished the upload. if so, add the file to state
      for (let i = 0; i < prevProps.upload.files.length; i++) {
        if (prevProps.upload.files[i].isUploading && !upload.files[i].isUploading && !upload.files[i].error) {
          // upload finished
          const mediaList = stateMediaList.slice();
          mediaList.splice(0, 0, upload.files[i].response);
          this.setState({ mediaList });
        }
      }
    }
  };

  componentDidMount = () => {
    window.addEventListener('resize', this.updateDimensions);
  };

  componentWillUnmount = () => {
    window.removeEventListener('resize', this.updateDimensions);
  };

  setActiveFilter = (e, activeFilter) => {
    e.preventDefault();
    e.stopPropagation();

    this.setState({ activeFilter });
  };

  updateDimensions = () => {
    let setState = false;
    const state = {};

    const headNode = findDOMNode(this.head);
    if (headNode !== null) {
      state.headDimensions = headNode.getBoundingClientRect();
      setState = true;
    }

    if (setState) {
      // console.log(state);
      this.setState(state);
    }
  };

  fetchInitialData = () => {
    this.setState({ activePage: 1, selected: null }, () => {
      this.fetchData();
    });
  };

  fetchData = () => {
    const { searchText, urlData, activePage, resPerPage } = this.state;
    const { list, fetch } = this.props;

    fetch(urlData, activePage, resPerPage, searchText, list.sort, list.dir);
  };

  doSort = eventKey => {
    const { fetch } = this.props;
    const { searchText, urlData, resPerPage } = this.state;
    const sort = eventKey.split(',');

    this.setState(
      {
        mediaList: [],
        activePage: 1,
        selected: null,
      },
      () => fetch(urlData, 1, resPerPage, searchText, sort[0], sort[1])
    );
  };

  changeDisplayMode = displayMode => {
    this.setState({ displayMode });
    localStorage.setItem('CmsMediaDisplayMode', displayMode);
  };

  loadMore = () => {
    // calculate if there are any pages we can load
    const { list } = this.props;
    const { activePage, resPerPage } = this.state;

    const maxPages = Math.ceil(list.data.count / resPerPage);

    if (!list.pending && activePage < maxPages) {
      this.setState(
        prevState => ({ activePage: prevState.activePage + 1 }),
        () => {
          this.fetchData();
        }
      );
    }
  };

  getWebFile = mediaFile => {
    let webFile;
    _forEach(mediaFile.variations, variation => {
      if (variation.variation_type === 'web') {
        webFile = variation.file;
      }
    });

    return webFile;
  };

  select = fileId => {
    this.setState(prevState => ({ selected: fileId === prevState.selected ? null : fileId }));
  };

  directSetParamsAndClose = fileId => {
    const { mediaList } = this.state;

    const selectedFile = _find(mediaList, ['id', fileId]);
    this.setParamsAndCloseForFile(selectedFile);
  };

  setParamsAndClose = () => {
    const { mediaList, selected } = this.state;

    const selectedFile = _find(mediaList, ['id', selected]);
    this.setParamsAndCloseForFile(selectedFile);
  };

  setParamsAndCloseForFile = selectedFile => {
    const { type, mediaType, setParams, close } = this.props;

    switch (mediaType) {
      case 'video': {
        const isUploaded =
          selectedFile.video_info &&
          selectedFile.video_info.thumb_transcoding_finished &&
          selectedFile.video_info.web_transcoding_finished
            ? '1'
            : '0';
        const thumbUrl = isUploaded ? selectedFile.video_info.thumb_link : '';
        const webMp4Url = isUploaded ? selectedFile.video_info.web_mp4_link : '';
        const webWebmUrl = isUploaded ? selectedFile.video_info.web_webm_link : '';

        setParams([
          { name: type + 'File', value: selectedFile.id },
          { name: type + 'Name', value: selectedFile.file_name },
          { name: type + 'Uploaded', value: isUploaded },
          { name: type + 'ThumbUrl', value: thumbUrl },
          { name: type + 'WebMp4Url', value: webMp4Url },
          { name: type + 'WebWebmUrl', value: webWebmUrl },
        ]);

        break;
      }

      default: {
        const webFile = this.getWebFile(selectedFile);
        let webLink = '';

        if (!webFile && selectedFile.web_link) {
          webLink = selectedFile.web_link;
        } else if (webFile) {
          webLink = webFile.web_link;
        }

        setParams([
          { name: type + 'File', value: selectedFile.id },
          { name: type + 'Name', value: selectedFile.file_name },
          { name: type + 'Url', value: webLink },
        ]);
      }
    }

    close();
  };

  manualUpload = files => {
    const { upload, uploadFile } = this.props;

    for (let i = 0; i < files.length; i++) {
      const file = files.item(i);
      uploadFile(file, upload.context.uploadLink, upload.context.data);
    }
  };

  doSearch = e => {
    e.preventDefault();
    e.stopPropagation(); // if we are editing a page, we have to make sure the parent form will not be submitted

    const { searchText, urlData, resPerPage } = this.state;
    const { list, fetch } = this.props;

    this.setState(
      {
        mediaList: [],
        activePage: 1,
        selected: null,
      },
      () => fetch(urlData, 1, resPerPage, searchText, list.sort, list.dir)
    );
  };

  delete = fileId => {
    const { deleteFile } = this.props;
    deleteFile(fileId);
  };

  handleScroll = e => {
    const {
      target: { scrollHeight, scrollTop, clientHeight },
    } = e;
    const bottom = scrollHeight - scrollTop === clientHeight;

    if (bottom) {
      this.loadMore();
    }
  };

  render() {
    const {
      intl: { messages },
      list,
      show,
      close,
      layout,
      upload,
      pageId,
      itemId,
      mediaType,
    } = this.props;
    const { mediaList, selected, displayMode, headDimensions, activeFilter } = this.state;

    const thumbnailSize = displayMode === 'th' ? '126px' : '220px';

    let mainHeight = 'auto';
    if (headDimensions.height) {
      mainHeight = layout.height - 60 - 25 - headDimensions.height + 'px';
    }

    return (
      <Modal show={show} onHide={close} onShow={this.updateDimensions} className="modal-wide modal-high">
        <Modal.Header closeButton ref={head => (this.head = head)}>
          <Modal.Title>
            {messages.media_files} {!list.pending && <span className="hint-text">({list.data.count})</span>}
          </Modal.Title>

          <div className="bg-master-lighter p-t-5 p-b-5">
            <Col xs={12} sm={7} className="p-t-5 text-left">
              {messages.choose_or_drop_file_to_upload} {messages.media_formats_supported}{' '}
              {messages.max_file_upload_size}
            </Col>
            <Col xs={12} sm={5} className="text-right">
              <DropdownButton
                bsSize="sm"
                noCaret
                title={
                  <span>
                    {messages.do_sort}: {messages.sort[list.sort]} <i className={'m-l-10 fa fa-sort-' + list.dir} />
                  </span>
                }
                onSelect={this.doSort}
              >
                <MenuItem eventKey="cdate,desc">{messages.sort.cdate}</MenuItem>
              </DropdownButton>

              <span className="m-l-10 p-l-10 b-l b-master" />

              <DropdownButton
                bsSize="sm"
                noCaret
                title={<i className={'fa fa-fw fa-' + displayMode} />}
                onSelect={this.changeDisplayMode}
              >
                <MenuItem eventKey="th">
                  <i className="fa fa-th" />
                </MenuItem>
                <MenuItem eventKey="th-large">
                  <i className="fa fa-th-large" />
                </MenuItem>
              </DropdownButton>

              <span className="m-l-10 p-l-10 b-l b-master" />

              <input
                type="file"
                ref={ref => (this.uploadInput = ref)}
                style={{ display: 'none' }}
                onChange={e => this.manualUpload(e.target.files)}
              />
              <Button bsSize="sm" className="btn-rounded" bsStyle="primary" onClick={() => this.uploadInput.click()}>
                <i className="fa fa-upload m-r-10" /> {messages.upload}
              </Button>
            </Col>
            <Clearfix />
          </div>
        </Modal.Header>
        <Modal.Body>
          <Grid fluid className="no-padding">
            <Row className="no-margin">
              <Col xsHidden sm={4} md={3} lg={2} className="no-padding">
                <nav className="secondary-sidebar padding-30 m-t-20" style={{ width: 'auto', float: 'none' }}>
                  <Form onSubmit={this.doSearch} className="m-b-20">
                    <FormGroup>
                      <InputGroup>
                        <FormControl type="text" onChange={e => this.setState({ searchText: e.target.value })} />
                        <InputGroup.Button>
                          <Button type="submit">
                            <i className="fas fa-search" />
                          </Button>
                        </InputGroup.Button>
                      </InputGroup>
                    </FormGroup>
                  </Form>

                  <ul className="main-menu">
                    <li className={!activeFilter ? 'active' : ''}>
                      <a href="#all" onClick={e => this.setActiveFilter(e, null)}>
                        <span className="title">{messages.all}</span>
                      </a>
                    </li>
                    <li className={activeFilter === 'self' ? 'active' : ''}>
                      <a href="#self" onClick={e => this.setActiveFilter(e, 'self')}>
                        <span className="title">{messages.uploaded_by_me}</span>
                      </a>
                    </li>
                    {!!itemId && (
                      <li className={activeFilter === 'item' ? 'active' : ''}>
                        <a href="#item" onClick={e => this.setActiveFilter(e, 'item')}>
                          <span className="title">{messages.current_item}</span>
                        </a>
                      </li>
                    )}
                    {!!pageId && (
                      <li className={activeFilter === 'page' ? 'active' : ''}>
                        <a href="#page" onClick={e => this.setActiveFilter(e, 'page')}>
                          <span className="title">{messages.current_page}</span>
                        </a>
                      </li>
                    )}
                  </ul>
                </nav>
              </Col>
              <Col xs={12} sm={8} md={9} lg={10} className="no-padding">
                {show && (
                  <div className="p-t-20">
                    <Col xs={selected ? 9 : 12} className="p-r-0 p-l-0">
                      <div
                        ref={this.scrollContainerRef}
                        className="p-l-20"
                        style={{
                          overflow: 'auto',
                          height: mainHeight,
                        }}
                        onScroll={this.handleScroll}
                      >
                        {/* show images that are being uploaded */}
                        {upload.isUploading &&
                          upload.files.map((uploadFile, i) => {
                            if (uploadFile.isUploading && uploadFile.context === 'cms') {
                              return (
                                <div
                                  key={i}
                                  className="pull-left m-r-10 m-b-10"
                                  style={{ width: thumbnailSize, height: thumbnailSize }}
                                >
                                  <CmsMediaSelectorUploadFilePreview upload={uploadFile} />
                                </div>
                              );
                            }

                            return null;
                          })}

                        {mediaList.map(mediaFile => {
                          return (
                            <div
                              key={mediaFile.id}
                              className="pull-left m-r-10 m-b-10"
                              style={{ width: thumbnailSize, height: thumbnailSize }}
                            >
                              <CmsMediaSelectorFilePreview
                                file={mediaFile}
                                mediaType={mediaType}
                                selected={selected === mediaFile.id}
                                onSelect={this.select}
                                onDelete={this.delete}
                                onDirectSelect={this.directSetParamsAndClose}
                              />
                            </div>
                          );
                        })}
                        <Clearfix />

                        {!list.pending && mediaList.length === 0 && (
                          <Alert bsStyle="info">
                            {messages.no_files_found} {messages.upload_file_by_dragging_over}
                          </Alert>
                        )}

                        {list.pending && <Loader />}
                      </div>
                    </Col>

                    {!!selected && (
                      <Col
                        xs={3}
                        className="no-padding b-l"
                        style={{
                          overflow: 'auto',
                          height: mainHeight,
                        }}
                      >
                        <div className="p-l-10 p-r-10">
                          <CmsMediaSelectorFileData
                            id={selected}
                            mediaType={mediaType}
                            selectFile={this.setParamsAndCloseForFile}
                          />
                        </div>
                      </Col>
                    )}

                    <Clearfix />
                  </div>
                )}
              </Col>
            </Row>
          </Grid>
        </Modal.Body>
      </Modal>
    );
  }
}
CmsMedia.defaultProps = {
  type: 'image',
  urlType: null,
};

const mapStateToProps = state => {
  return {
    list: state.list.cmsMedia,
    layout: state.layout,
    user: state.login.user,
    upload: state.upload,
    fileDeleter: state.deleter.file,
  };
};
const mapDispatchToProps = dispatch => {
  return {
    updateUploadContext: newContext => {
      dispatch(changeUploadContext(newContext));
    },
    doResetUploadContext: () => {
      dispatch(resetUploadContext());
    },
    changeSort: (listName, sort, dir) => {
      dispatch(changeListSort(listName, sort, dir));
    },
    fetch: (data, page = 1, resPerPage = 20, search = '', sort = '', dir = '') => {
      return listDispatchHelper(
        'cmsMedia',
        '/api/cmsMedia/files.json',
        { ...data, offset: (page - 1) * resPerPage, limit: resPerPage, search, sort, dir },
        dispatch
      );
    },
    uploadFile: fileUploadHelper(dispatch),
    deleteFile: fileId => {
      deleterDispatchHelper('file', `/api/cmsMedia/files/${fileId}.json`, dispatch);
    },
  };
};

export default injectIntl(
  connect(
    mapStateToProps,
    mapDispatchToProps
  )(CmsMedia)
);
